import { useSearchParams } from 'react-router-dom';
import type { PlanStep, StepResult } from '@verifyloop/core';
import {
  VariantA_PipelineFlow,
  VariantB_VerticalFlowchart,
  VariantC_PhasedStages,
  VariantD_CompactGridFlow,
  type PlaybookFlowProps,
} from './PlaybookFlowVariants';
import { N8nPlaybookCanvas } from './N8nPlaybookCanvas';
import { PrototypeSwitcher, type PrototypeVariant } from './PrototypeSwitcher';

const PROTOTYPE_VARIANTS: readonly PrototypeVariant[] = [
  { id: 'N', name: 'n8n Workflow Canvas' },
  { id: 'A', name: 'Horizontal Pipeline' },
  { id: 'D', name: 'Compact Grid (Zero-Scroll)' },
  { id: 'B', name: 'Vertical Flowchart' },
  { id: 'C', name: 'Phased Stages' },
] as const;

export interface PlaybookFlowViewProps extends Omit<PlaybookFlowProps, 'steps'> {
  steps: readonly PlanStep[];
  stepResults?: readonly StepResult[];
  showSwitcher?: boolean;
  onInsertStep?: (afterIndex: number) => void;
  onSimulateStep?: (index: number) => void;
  onAddBranch?: () => void;
  onExportPlaywright?: () => void;
}

export function PlaybookFlow({
  steps,
  stepResults,
  selectedStepIndex,
  onSelectStep,
  activeRunningIndex,
  showSwitcher = false,
  onInsertStep,
  onSimulateStep,
  onAddBranch,
  onExportPlaywright,
}: PlaybookFlowViewProps): JSX.Element {
  const [params, setParams] = useSearchParams();
  const currentVariant = params.get('variant') ?? 'N';

  const handleSelectVariant = (nextVariant: string) => {
    const draft = new URLSearchParams(params);
    draft.set('variant', nextVariant);
    setParams(draft, { replace: true });
  };

  const flowProps: PlaybookFlowProps = {
    steps,
    stepResults,
    selectedStepIndex,
    onSelectStep,
    activeRunningIndex,
  };

  return (
    <div className="playbook-flow-container" style={{ position: 'relative', width: '100%' }}>
      {currentVariant === 'N' ? (
        <N8nPlaybookCanvas
          {...flowProps}
          onInsertStep={onInsertStep}
          onSimulateStep={onSimulateStep}
          onAddBranch={onAddBranch}
          onExportPlaywright={onExportPlaywright}
          height={560}
        />
      ) : currentVariant === 'D' ? (
        <VariantD_CompactGridFlow {...flowProps} />
      ) : currentVariant === 'B' ? (
        <VariantB_VerticalFlowchart {...flowProps} />
      ) : currentVariant === 'C' ? (
        <VariantC_PhasedStages {...flowProps} />
      ) : (
        <VariantA_PipelineFlow {...flowProps} />
      )}

      {showSwitcher ? (
        <PrototypeSwitcher
          variants={PROTOTYPE_VARIANTS}
          current={currentVariant}
          onSelect={handleSelectVariant}
        />
      ) : null}
    </div>
  );
}
